import { v4 as uuidv4 } from 'uuid';
import { useCallback } from 'react';
import { FieldArray, FieldArrayRenderProps } from 'react-final-form-arrays';
import { FormSpy } from 'react-final-form';
import { getIn } from 'final-form';

import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { Button } from 'UIPackage/MainModule/components/Button';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { IKnmActFamiliarizationNotificationsForm } from 'KnmErknmPackage/MainModule/interfaces/IKnmActFamiliarizationNotificationsForm';
import { IDecisionFormRef } from 'DecisionPackage/MainModule/interfaces/IDecisionFormRef';
import { Hint } from 'CorePackage/MainModule/components/Hint';
import { getFieldArrayErrorMessage } from 'CorePackage/MainModule/common/getFieldArrayErrorMessage';

import { ResultDecisionsItemContentIntroductionsItem } from './ResultDecisionsItemContentIntroductionsItem';
import styles from './ResultDecisionsItemContentIntroductions.module.scss';
import { getTitleFieldWithRedStar } from '../../../../../../functions/getTitleFieldWithRedStar';

interface IResultDecisionsItemContentIntroductionsProps {
  id: string;
  name: string;
  formRef: IKnmFormRef | IDecisionFormRef | undefined;
  isDecision?: boolean;
  isPm?: boolean;
}

export const ResultDecisionsItemContentIntroductions = ({
  id,
  name,
  formRef,
  isDecision,
  isPm,
}: IResultDecisionsItemContentIntroductionsProps) => {
  const remove = formRef?.current.mutators.remove;
  const push = formRef?.current.mutators.push;
  const familiarizationNotificationsFieldName = `${name}.familiarizationNotifications`;

  const addNotify = useCallback(() => {
    if (push) {
      const addingEvent: IKnmActFamiliarizationNotificationsForm = {
        listId: uuidv4(),
        isNew: true,
        existedAtSigning: false,
        knmNoticeType: undefined,
        note: undefined,
        noticeDate: undefined,
      };

      push(familiarizationNotificationsFieldName, addingEvent);
    }
  }, [familiarizationNotificationsFieldName, push]);

  const makeRemoveNotifyHandler = (index: number, isNew: boolean) => () =>
    isNew && remove && remove(familiarizationNotificationsFieldName, index);

  return (
    <FieldBlock
      id={`${id}.familiarizationNotifications`}
      className={styles.GroupMarginBottom}
      margin="top"
      label={getTitleFieldWithRedStar({
        label: 'Уведомление контролируемого лица о предписании',
        field: familiarizationNotificationsFieldName,
        isPm,
        isDecision,
        isKnm: !isPm && !isDecision,
      })}
      button={
        <Button
          className="shared-btn-add"
          text="Добавить"
          variant="link"
          color="primary"
          type="button"
          onClick={addNotify}
        />
      }
    >
      <>
        <FormSpy subscription={{ errors: true }}>
          {({ errors }) => {
            const errorMessage = getFieldArrayErrorMessage(getIn(errors || {}, familiarizationNotificationsFieldName));

            if (!errorMessage) {
              return null;
            }

            return <p className={`${styles.ErrorMessage} shared-input-error`}>{errorMessage}</p>;
          }}
        </FormSpy>
        <span className={styles.Message}>
          <Hint
            text="Если уведомление контролируемого лица производится через портал Госуслуги, соответствующая информация размещается и
            обновляется автоматически. Вручную указывать такие сведения не требуется."
          />
        </span>
        <FieldArray name={familiarizationNotificationsFieldName}>
          {({ fields }: FieldArrayRenderProps<IKnmActFamiliarizationNotificationsForm, HTMLElement>) => {
            if (!fields.length) {
              return null;
            }

            return fields.map((fieldName, sIndex) => {
              const fieldValue: IKnmActFamiliarizationNotificationsForm = fields.value?.[sIndex];
              const itemId = `${id}.familiarizationNotifications[${fieldValue.listId}]`;

              return (
                <ResultDecisionsItemContentIntroductionsItem
                  fieldValue={fieldValue}
                  id={itemId}
                  key={itemId}
                  isDisabledField={!fieldValue.isNew}
                  name={fieldName}
                  remove={makeRemoveNotifyHandler(sIndex, fieldValue.isNew)}
                  isDecision={isDecision}
                  isPm={isPm}
                />
              );
            });
          }}
        </FieldArray>
      </>
    </FieldBlock>
  );
};
